import { useState, useEffect } from 'react';
import api from '../api/axios';

// ---------------------------------------------------------------------------
// Admin form for employer Hiring plans (Free / Starter / Growth / Pro ...).
// Everything the employer pricing page shows comes from what is saved here.
// Nothing is hard-coded: prices, limits, toggles and bullet points are all admin data.
// ---------------------------------------------------------------------------

const BILLING_TYPES = [
  { value: 'free', label: 'Free' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
  { value: 'one_time', label: 'One-time' },
];

// key, label, step, placeholder (blank behaviour is explained in the placeholder)
const NUMBER_FIELDS = [
  { key: 'price_monthly', label: 'Monthly price (₹)', step: '0.01', placeholder: '0' },
  { key: 'price_yearly', label: 'Yearly price (₹)', step: '0.01', placeholder: '0' },
  { key: 'display_order', label: 'Display order', step: '1', placeholder: '1' },
  { key: 'pack_validity_days', label: 'Plan length (days)', step: '1', placeholder: '30' },
  { key: 'job_post_limit', label: 'Active jobs', step: '1', placeholder: 'Unlimited' },
  { key: 'job_validity_days', label: 'Job validity (days)', step: '1', placeholder: 'e.g. 30' },
  { key: 'application_limit', label: 'Applications per job', step: '1', placeholder: 'Unlimited' },
  { key: 'contact_unlocks_per_job', label: 'Candidate unlocks / month', step: '1', placeholder: 'Unlimited' },
  { key: 'featured_jobs', label: 'Featured jobs / month', step: '1', placeholder: '0' },
  { key: 'boost_credits', label: 'Job boosts / month', step: '1', placeholder: '0' },
  { key: 'recruiter_seats', label: 'Recruiter seats', step: '1', placeholder: '1' },
];

const NULLABLE_NUMBERS = [
  'pack_validity_days',
  'job_post_limit',
  'job_validity_days',
  'application_limit',
  'contact_unlocks_per_job',
];

const YEARLY_FIELDS = [
  { monthly: 'contact_unlocks_per_job', yearly: 'contact_unlocks_yearly', label: 'Candidate unlocks / year', placeholder: 'Unlimited' },
  { monthly: 'featured_jobs', yearly: 'featured_jobs_yearly', label: 'Featured jobs / year', placeholder: '0' },
  { monthly: 'boost_credits', yearly: 'boost_credits_yearly', label: 'Job boosts / year', placeholder: '0' },
];

const autoYearly = (monthlyVal) => {
  const raw = String(monthlyVal ?? '').trim();
  return raw === '' ? '' : String(Number(raw) * 12);
};
const isCustom = (form, yf) => !!form[`${yf.yearly}_custom`];
const yearlyValue = (form, yf) => (isCustom(form, yf) ? form[yf.yearly] : autoYearly(form[yf.monthly]));

const STATUS_FLAGS = [
  ['is_active', 'Active (visible to employers)'],
  ['is_best_value', 'Best Value badge'],
  ['is_featured_pack', 'Featured'],
];

const FEATURE_FLAGS = [
  ['ai_features_enabled', 'AI features'],
  ['candidate_search_enabled', 'Candidate search'],
  ['advanced_filters_enabled', 'Advanced filters'],
  ['ai_matching_enabled', 'AI matching'],
  ['ai_shortlist_enabled', 'AI shortlist'],
  ['interview_tools_enabled', 'Interview tools'],
  ['analytics_enabled', 'Analytics'],
  ['whatsapp_contact_enabled', 'WhatsApp contact'],
  ['bulk_actions_enabled', 'Bulk actions'],
  ['priority_support', 'Priority support'],
];

const emptyForm = () => {
  const f = {
    name: '',
    code: '',
    billing_type: 'monthly',
    short_description: '',
    cta_label: '',
    featuresText: '',
    is_active: true,
  };
  NUMBER_FIELDS.forEach((n) => { f[n.key] = ''; });
  f.recruiter_seats = '1';
  [...STATUS_FLAGS, ...FEATURE_FLAGS].forEach(([k]) => { if (!(k in f)) f[k] = false; });
    YEARLY_FIELDS.forEach((yf) => { f[yf.yearly] = ''; f[`${yf.yearly}_custom`] = false; });
  return f;
};

const toForm = (p) => {
  const f = emptyForm();
  f.name = p.name ?? '';
  f.code = p.code ?? '';
  f.billing_type = p.billing_type ?? 'monthly';
  f.short_description = p.short_description ?? '';
  f.cta_label = p.cta_label ?? '';
  f.featuresText = Array.isArray(p.features) ? p.features.join('\n') : '';
  NUMBER_FIELDS.forEach((n) => { f[n.key] = p[n.key] == null ? '' : String(p[n.key]); });
  [...STATUS_FLAGS, ...FEATURE_FLAGS].forEach(([k]) => { f[k] = !!p[k]; });
    YEARLY_FIELDS.forEach((yf) => {
    if (p[yf.yearly] != null) {
      f[yf.yearly] = String(p[yf.yearly]);
      f[`${yf.yearly}_custom`] = true;
    }
  });
  return f;
};

const toPayload = (f) => {
  const payload = {
    name: f.name.trim(),
    code: f.code.trim() || null,
    billing_type: f.billing_type,
    short_description: f.short_description.trim() || null,
    cta_label: f.cta_label.trim() || null,
    features: f.featuresText.split('\n').map((s) => s.trim()).filter(Boolean),
  };

  NUMBER_FIELDS.forEach((n) => {
    const raw = String(f[n.key] ?? '').trim();
    if (NULLABLE_NUMBERS.includes(n.key)) {
      payload[n.key] = raw === '' ? null : Number(raw); // blank = unlimited / not set
    } else if (n.key === 'recruiter_seats') {
      payload[n.key] = raw === '' ? 1 : Number(raw);
    } else {
      payload[n.key] = raw === '' ? 0 : Number(raw);
    }
  });

  [...STATUS_FLAGS, ...FEATURE_FLAGS].forEach(([k]) => { payload[k] = !!f[k]; });
    YEARLY_FIELDS.forEach((yf) => {
    const raw = String(f[yf.yearly] ?? '').trim();
    payload[yf.yearly] = isCustom(f, yf) && raw !== '' ? Number(raw) : null; // null = auto (monthly x 12)
  });
  
  return payload;
};

const errText = (e, fallback) => {
  const errs = e.response?.data?.errors;
  return errs ? Object.values(errs).flat().join(' ') : (e.response?.data?.message || fallback);
};

const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent';

// Defined at module level so inputs keep focus while typing.
function PlanForm({ form, onChange, onSubmit, saving, submitLabel, onCancel, onDelete, deleting }) {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <label className="text-xs text-gray-500">Plan name
          <input type="text" value={form.name} onChange={(e) => onChange('name', e.target.value)}
            placeholder="e.g. Growth" className={`${inputCls} mt-1`} />
        </label>
        <label className="text-xs text-gray-500">Plan code (slug)
          <input type="text" value={form.code} onChange={(e) => onChange('code', e.target.value)}
            placeholder="auto from name if blank" className={`${inputCls} mt-1`} />
        </label>
        <label className="text-xs text-gray-500">Billing type
          <select value={form.billing_type} onChange={(e) => onChange('billing_type', e.target.value)}
            className={`${inputCls} mt-1`}>
            {BILLING_TYPES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
          </select>
        </label>
        <label className="text-xs text-gray-500 sm:col-span-2">Short description
          <input type="text" value={form.short_description} onChange={(e) => onChange('short_description', e.target.value)}
            placeholder="Who this plan is for" className={`${inputCls} mt-1`} />
        </label>
        <label className="text-xs text-gray-500">Button label
          <input type="text" value={form.cta_label} onChange={(e) => onChange('cta_label', e.target.value)}
            placeholder="e.g. Choose Growth" className={`${inputCls} mt-1`} />
        </label>
      </div>

      <div>
        <p className="text-xs font-semibold text-gray-500 mb-2">Price and limits</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {NUMBER_FIELDS.map((n) => (
            <label key={n.key} className="text-xs text-gray-500">{n.label}
              <input type="number" min="0" step={n.step} value={form[n.key]}
                onChange={(e) => onChange(n.key, e.target.value)}
                placeholder={n.placeholder} className={`${inputCls} mt-1`} />
            </label>
          ))}
        </div>
        <p className="text-[11px] text-gray-400 mt-2">
          Yearly price is entered here, never calculated. Leave Active jobs, Applications and Candidate unlocks blank for unlimited.
        </p>
      </div>

            <div>
        <p className="text-xs font-semibold text-gray-500 mb-2">
          Yearly limits <span className="font-normal text-gray-400">(auto = monthly × 12, you can change them)</span>
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {YEARLY_FIELDS.map((yf) => {
            const custom = isCustom(form, yf);
            return (
              <label key={yf.yearly} className="text-xs text-gray-500">{yf.label}
                <input
                  type="number" min="0" step="1"
                  value={yearlyValue(form, yf)}
                  placeholder={yf.placeholder}
                  onChange={(e) => {
                    onChange(yf.yearly, e.target.value);
                    onChange(`${yf.yearly}_custom`, true);
                  }}
                  className={`${inputCls} mt-1 ${custom ? 'border-purple-300' : ''}`}
                />
                {custom ? (
                  <button type="button"
                    onClick={() => { onChange(`${yf.yearly}_custom`, false); onChange(yf.yearly, ''); }}
                    className="text-[11px] text-[#7C3AED] hover:underline mt-1">
                    Custom · reset to ×12
                  </button>
                ) : (
                  <span className="block text-[11px] text-gray-400 mt-1">Auto · monthly × 12</span>
                )}
              </label>
            );
          })}
        </div>
      </div>

      <div>
        <p className="text-xs font-semibold text-gray-500 mb-2">Status</p>
        <div className="flex flex-wrap gap-x-5 gap-y-2">
          {STATUS_FLAGS.map(([k, label]) => (
            <label key={k} className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={!!form[k]} onChange={(e) => onChange(k, e.target.checked)} />
              {label}
            </label>
          ))}
        </div>
        <p className="text-[11px] text-gray-400 mt-2">Only one plan can carry the Best Value badge. Saving a new one removes it from the other.</p>
      </div>

      <div>
        <p className="text-xs font-semibold text-gray-500 mb-2">Included features</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-5 gap-y-2">
          {FEATURE_FLAGS.map(([k, label]) => (
            <label key={k} className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={!!form[k]} onChange={(e) => onChange(k, e.target.checked)} />
              {label}
            </label>
          ))}
        </div>
      </div>

      <label className="block text-xs text-gray-500">
        Feature list (one bullet per line). The first 6 lines show on the plan card; the rest open under "View all features".
        <textarea rows={8} value={form.featuresText} onChange={(e) => onChange('featuresText', e.target.value)}
          className={`${inputCls} mt-1 font-mono`} />
      </label>

      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={onSubmit} disabled={saving || !form.name.trim()}
          className="bg-gradient-to-r from-purple-600 to-pink-500 text-white px-5 py-2 rounded-lg text-sm font-semibold hover:opacity-90 disabled:opacity-50">
          {saving ? 'Saving...' : submitLabel}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-500 hover:bg-gray-100">
            Close
          </button>
        )}
        {onDelete && (
          <button type="button" onClick={onDelete} disabled={deleting}
            className="ml-auto bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:opacity-90 disabled:opacity-50">
            {deleting ? 'Deleting...' : 'Delete'}
          </button>
        )}
      </div>
    </div>
  );
}

export default function PlansAdminPanel() {
  const [plans, setPlans] = useState([]);
  const [forms, setForms] = useState({});
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newForm, setNewForm] = useState(emptyForm());
  const [adding, setAdding] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const load = async () => {
    setLoading(true);
    setErr('');
    try {
      const { data } = await api.get('/admin/plans');
      const list = data.filter((p) => p.type !== 'hiring');
      setPlans(list);
      const f = {};
      list.forEach((p) => { f[p.id] = toForm(p); });
      setForms(f);
    } catch (e) {
      setErr(errText(e, 'Failed to load plans.'));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const setField = (id, key, value) =>
    setForms((prev) => ({ ...prev, [id]: { ...prev[id], [key]: value } }));

  // After a save that turned Best Value on, turn it off locally on the others.
  const applySaved = (saved) => {
    setPlans((prev) => prev
      .map((p) => (p.id === saved.id ? saved : (saved.is_best_value && p.type === saved.type ? { ...p, is_best_value: false } : p)))
      .sort((a, b) => (a.display_order - b.display_order) || (a.id - b.id)));
    setForms((prev) => {
      const next = { ...prev, [saved.id]: toForm(saved) };
      if (saved.is_best_value) {
        Object.keys(next).forEach((id) => {
          if (Number(id) !== saved.id) next[id] = { ...next[id], is_best_value: false };
        });
      }
      return next;
    });
  };

  const savePlan = async (id) => {
    setSavingId(id);
    setErr('');
    setMsg('');
    try {
      const { data } = await api.put(`/admin/plans/${id}`, toPayload(forms[id]));
      applySaved(data);
      setMsg(`${data.name} saved.`);
    } catch (e) {
      setErr(errText(e, 'Failed to save plan.'));
    } finally {
      setSavingId(null);
    }
  };

  const addPlan = async () => {
    setAdding(true);
    setErr('');
    setMsg('');
    try {
      const { data } = await api.post('/admin/plans', { ...toPayload(newForm), type: 'subscription' });
      applySaved(data);
      setNewForm(emptyForm());
      setShowAdd(false);
      setOpenId(null);
      setMsg(`${data.name} plan created.`);
    } catch (e) {
      setErr(errText(e, 'Failed to create plan.'));
    } finally {
      setAdding(false);
    }
  };

  const deletePlan = async (plan) => {
    if (!window.confirm(`Delete "${plan.name}"? If it has subscriptions, deactivate it instead.`)) return;
    setDeletingId(plan.id);
    setErr('');
    setMsg('');
    try {
      await api.delete(`/admin/plans/${plan.id}`);
      setPlans((prev) => prev.filter((p) => p.id !== plan.id));
      setOpenId(null);
      setMsg(`${plan.name} deleted.`);
    } catch (e) {
      setErr(errText(e, 'Failed to delete plan.'));
    } finally {
      setDeletingId(null);
    }
  };

  const fmtPrice = (n) => (Number(n) > 0 ? `₹${Number(n).toLocaleString('en-IN')}` : '₹0');

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <h3 className="font-bold text-[#0D1B3D] text-lg mb-1">Hiring Plans</h3>
          <p className="text-sm text-gray-500">
            Everything employers see on the pricing page comes from these plans. Inactive plans are hidden.
          </p>
        </div>
        <button type="button" onClick={() => { setShowAdd((s) => !s); setOpenId(null); }}
          className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:opacity-90">
          {showAdd ? 'Close' : 'Add plan'}
        </button>
      </div>

      {err && <p className="text-sm text-red-600 mb-2">{err}</p>}
      {msg && <p className="text-sm text-green-600 mb-2">{msg}</p>}

      {showAdd && (
        <div className="border border-purple-100 bg-purple-50/30 rounded-xl p-4 mb-4">
          <p className="text-sm font-semibold text-[#0D1B3D] mb-3">New plan</p>
          <PlanForm
            form={newForm}
            onChange={(k, v) => setNewForm((f) => ({ ...f, [k]: v }))}
            onSubmit={addPlan}
            saving={adding}
            submitLabel="Create plan"
          />
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-400">Loading plans...</p>
      ) : plans.length === 0 ? (
        <p className="text-sm text-gray-400">No plans yet. Use "Add plan" to create the first one.</p>
      ) : (
        <div className="space-y-3">
          {plans.map((plan) => {
            const isOpen = openId === plan.id;
            return (
              <div key={plan.id} className="border border-gray-100 rounded-xl">
                <button type="button" onClick={() => { setOpenId(isOpen ? null : plan.id); setShowAdd(false); }}
                  className="w-full flex items-center gap-3 flex-wrap text-left px-4 py-3 hover:bg-gray-50 rounded-xl">
                  <span className="font-semibold text-[#0D1B3D]">{plan.name}</span>
                  {plan.code && <span className="text-xs text-gray-400">{plan.code}</span>}
                  <span className="text-sm text-gray-600">{fmtPrice(plan.price_monthly)}/month</span>
                  {Number(plan.price_yearly) > 0 && (
                    <span className="text-xs text-gray-400">{fmtPrice(plan.price_yearly)}/year</span>
                  )}
                  {plan.is_best_value && (
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-pink-50 text-pink-600">Best Value</span>
                  )}
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${plan.is_active ? 'bg-[#ECFDF5] text-[#10B981]' : 'bg-gray-100 text-gray-500'}`}>
                    {plan.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <span className="ml-auto text-xs font-semibold text-[#7C3AED]">{isOpen ? 'Close' : 'Edit'}</span>
                </button>

                {isOpen && forms[plan.id] && (
                  <div className="px-4 pb-4 pt-2 border-t border-gray-100">
                    <PlanForm
                      form={forms[plan.id]}
                      onChange={(k, v) => setField(plan.id, k, v)}
                      onSubmit={() => savePlan(plan.id)}
                      saving={savingId === plan.id}
                      submitLabel="Save changes"
                      onCancel={() => setOpenId(null)}
                      onDelete={() => deletePlan(plan)}
                      deleting={deletingId === plan.id}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}